import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  IconButton,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Savings as SavingsIcon,
  Home as HomeIcon,
  DirectionsCar as CarIcon,
  Restaurant as FoodIcon,
  LocalHospital as HealthIcon,
  ShoppingCart as ShoppingCartIcon,
  School as EducationIcon,
  Theaters as EntertainmentIcon,
  Power as UtilitiesIcon,
  Category as OtherIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Layout from '../components/Layout';
import BudgetForm from '../components/BudgetForm';
import { colors } from '../styles';
import * as budgetService from '../services/budgetService';
import BudgetCategoryEditModal from '../components/BudgetCategoryEditModal';
import BudgetSelectorModal from '../components/BudgetSelectorModal';
import MonthSelector from '../components/MonthSelector';
import BudgetCategoryDetailsModal from '../components/BudgetCategoryDetailsModal';
import CountUp from 'react-countup';
import * as api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '20px',
  backgroundColor: colors.white,
  boxShadow: 'none',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: 'none'
  }
}));

const CategoryIcon = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: `${colors.primary}10`,
  marginBottom: theme.spacing(2),
  position: 'relative',
  '& svg': {
    color: colors.primary,
    fontSize: '28px'
  }
}));

const CategoryProgress = styled(CircularProgress)(({ theme }) => ({
  position: 'absolute',
  top: -4,
  left: -4,
  right: -4,
  bottom: -4,
  '& .MuiCircularProgress-circle': {
    strokeLinecap: 'round',
    color: colors.ready,
  }
}));

const StatCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '24px',
  backgroundColor: colors.white,
  boxShadow: 'none',
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  border: `2px solid ${colors.primary}10`,
  transition: 'all 0.3s ease-in-out',
  minHeight: '160px',
  '&:hover': {
    transform: 'translateY(-4px)',
    border: `2px solid ${colors.primary}20`,
    '& .MuiSvgIcon-root': {
      transform: 'scale(1.1)'
    }
  }
}));

const StatIcon = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: `${colors.primary}10`,
  transition: 'all 0.3s ease-in-out',
  '& .MuiSvgIcon-root': {
    fontSize: '28px',
    color: colors.primary,
    transition: 'all 0.3s ease-in-out'
  }
}));

const BudgetCard = styled(Card)(({ theme, active }) => ({
  borderRadius: '20px',
  padding: theme.spacing(2),
  cursor: 'pointer',
  backgroundColor: active ? `${colors.primary}10` : colors.white,
  border: active ? `2px solid ${colors.primary}` : '2px solid transparent',
  transition: 'all 0.2s ease-in-out',
  boxShadow: 'none',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: 'none'
  }
}));

const CircularProgressWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '80px',
  height: '80px',
  marginRight: theme.spacing(2)
}));

const CircularProgressLabel = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column'
}));

const CategoryCardCircle = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 200,
  height: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '12px auto 0 auto',
}));

const CategoryCardInner = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
}));

const CategoryCardIcon = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '& svg': {
    fontSize: 32,
    color: colors.primary,
  }
}));

const ICONS = {
  home: <HomeIcon />,
  car: <CarIcon />,
  food: <FoodIcon />,
  health: <HealthIcon />,
  shopping: <ShoppingCartIcon />,
  education: <EducationIcon />,
  entertainment: <EntertainmentIcon />,
  utilities: <UtilitiesIcon />,
  other: <OtherIcon />
};

const Budgets = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [activeBudget, setActiveBudget] = useState(null);
  const [insights, setInsights] = useState(null);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isCategoryEditModalOpen, setIsCategoryEditModalOpen] = useState(false);
  const [showBudgetSelector, setShowBudgetSelector] = useState(false);
  const [selectedCategoryForBudgetChoice, setSelectedCategoryForBudgetChoice] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedCategoryForDetails, setSelectedCategoryForDetails] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const response = await budgetService.getBudgets();
      setBudgets(response);
    } catch (err) {
      console.error('Error fetching budgets:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (year, month) => {
    try {
      console.log('Fetching transactions for:', { year, month });
      const filters = {};
      if (year && month) {
        filters.year = year;
        filters.month = month; // Assuming month is 1-indexed
      }
      // Always fetch a larger limit to cover the month, consider pagination if truly large datasets
      const response = await api.getTransactions(1, 500, filters);
      console.log('Transactions fetched:', response);
      if (response && response.transactions) {
        setTransactions(response.transactions);
      } else {
        console.warn('Unexpected response format for transactions:', response);
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]); // Clear transactions on error
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Fetch budgets first
        const budgetResponse = await budgetService.getBudgets();
        setBudgets(budgetResponse);

        // Get available months from budgets or default
        const availableMonthsFromBudgets = getAvailableMonths(budgetResponse); // Pass budgets to derive months if needed
        setAvailableMonths(availableMonthsFromBudgets);
        
        let currentYearToFetch = new Date().getFullYear();
        let currentMonthToFetch = new Date().getMonth() + 1; // 1-indexed for API

        // Determine the month to load initially for insights and transactions
        if (availableMonthsFromBudgets.length > 0 && !selectedMonth) {
          // If selectedMonth is not set, use the latest available month
          setSelectedMonth(availableMonthsFromBudgets[0]);
          const [year, month] = availableMonthsFromBudgets[0].split('-').map(Number);
          currentYearToFetch = year;
          currentMonthToFetch = month;
        } else if (selectedMonth) {
          // If selectedMonth is already set (e.g. by user or previous state)
          const [year, month] = selectedMonth.split('-').map(Number);
          currentYearToFetch = year;
          currentMonthToFetch = month;
        }
        // If no available months and no selectedMonth, it defaults to current actual Y/M
        
        await fetchTransactions(currentYearToFetch, currentMonthToFetch);
        
        // Fetch insights for all budgets with the determined month
        if (budgetResponse.length > 0) {
          const allInsights = { budgetInsights: {} };
          await Promise.all(budgetResponse.map(async (budget) => {
            const insightsResponse = await budgetService.getBudgetInsights(budget._id, { month: `${currentYearToFetch}-${String(currentMonthToFetch).padStart(2, '0')}` });
            allInsights.budgetInsights[budget._id] = insightsResponse.data;
          }));
          setInsights(allInsights);

          if (budgetResponse.length === 1 && !activeBudget) {
            setActiveBudget(budgetResponse[0]);
            // Insights for the single active budget are already in allInsights
          }
        }

      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]); // Re-run when selectedMonth changes

  const getAvailableMonths = (currentBudgets) => { // Accept budgets as param
    // Return months from October 2023 to current month
    const months = [];
    const startDate = new Date(2023, 9); // October 2023 (0-based months)
    const endDate = new Date();
    
    for (let d = new Date(startDate); d <= endDate; d.setMonth(d.getMonth() + 1)) {
      const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push(monthStr);
    }
    
    return months.sort().reverse(); // Most recent first
  };

  const handleCreateBudget = async (budgetData) => {
    try {
      const response = await budgetService.createBudget(budgetData);
      setShowBudgetForm(false);
      await fetchBudgets();
      // Set as active budget and fetch insights
      setActiveBudget(response.data);
      const insightsResponse = await budgetService.getBudgetInsights(response.data._id, { month: selectedMonth });
      setInsights({ budgetInsights: { [response.data._id]: insightsResponse.data } });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateBudget = async (budgetData) => {
    try {
      await budgetService.updateBudget(editingBudget._id, budgetData);
      setEditingBudget(null);
      await fetchBudgets();
      // Refresh insights if this is the active budget
      if (activeBudget?._id === editingBudget._id) {
        const insightsResponse = await budgetService.getBudgetInsights(editingBudget._id, { month: selectedMonth });
        setInsights({ budgetInsights: { [editingBudget._id]: insightsResponse.data } });
      } else {
        // If no active budget, refresh all insights
        const allInsights = { budgetInsights: {} };
        await Promise.all(budgets.map(async (b) => {
          const insightsResponse = await budgetService.getBudgetInsights(b._id, { month: selectedMonth });
          allInsights.budgetInsights[b._id] = insightsResponse.data;
        }));
        setInsights(allInsights);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteBudget = async (budgetId) => {
    try {
      await budgetService.deleteBudget(budgetId);
      setEditingBudget(null);
      // Clear insights if this was the active budget
      if (activeBudget?._id === budgetId) {
        setActiveBudget(null);
        setInsights(null);
      }
      await fetchBudgets();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCategoryEditClick = (budget, category) => {
    // If a specific budget is provided, use it directly
    if (budget) {
      setSelectedCategory({ ...category, budgetId: budget._id });
      setIsCategoryEditModalOpen(true);
      return;
    }

    // For combined categories, check how many budgets have this category
    const budgetsWithCategory = budgets.filter(b => 
      b.categories?.some(c => c.name === category.name)
    );

    // If category exists in only one budget, edit it directly
    if (budgetsWithCategory.length === 1) {
      const budgetCategory = budgetsWithCategory[0].categories.find(c => c.name === category.name);
      setSelectedCategory({ ...budgetCategory, budgetId: budgetsWithCategory[0]._id });
      setIsCategoryEditModalOpen(true);
      return;
    }

    // Otherwise, show budget selector
    setSelectedCategoryForBudgetChoice(category);
    setShowBudgetSelector(true);
  };

  const handleBudgetSelect = (budget, budgetCategory) => {
    setShowBudgetSelector(false);
    setSelectedCategory({ ...budgetCategory, budgetId: budget._id });
    setIsCategoryEditModalOpen(true);
  };

  const handleCategoryUpdate = async (updatedCategory) => {
    try {
      if (selectedCategory.budgetId === 'all') {
        // Update the category in all relevant budgets
        await Promise.all(selectedCategory.budgetIds.map(budgetId =>
          budgetService.updateBudgetCategory(
            budgetId,
            selectedCategory._id,
            updatedCategory
          )
        ));
      } else {
        // Update in single budget
        await budgetService.updateBudgetCategory(
          selectedCategory.budgetId,
          selectedCategory._id,
          updatedCategory
        );
      }
      
      setIsCategoryEditModalOpen(false);
      // Refresh budgets
      await fetchBudgets();
      
      // Refresh insights with current month
      const allInsights = { budgetInsights: {} };
      await Promise.all(budgets.map(async (budget) => {
        const insightsResponse = await budgetService.getBudgetInsights(budget._id, { month: selectedMonth });
        allInsights.budgetInsights[budget._id] = insightsResponse.data;
      }));
      setInsights(allInsights);
      
    } catch (error) {
      console.error('Failed to update category:', error);
      setError(error.message);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('home') || name.includes('rent') || name.includes('mortgage')) return <HomeIcon />;
    if (name.includes('car') || name.includes('transport')) return <CarIcon />;
    if (name.includes('food') || name.includes('grocery') || name.includes('restaurant')) return <FoodIcon />;
    if (name.includes('health') || name.includes('medical')) return <HealthIcon />;
    if (name.includes('shopping')) return <ShoppingCartIcon />;
    if (name.includes('education') || name.includes('school')) return <EducationIcon />;
    if (name.includes('entertainment') || name.includes('fun')) return <EntertainmentIcon />;
    if (name.includes('utilities') || name.includes('bills')) return <UtilitiesIcon />;
    if (name.includes('savings') || name.includes('investment')) return <SavingsIcon />;
    return <OtherIcon />;
  };

  const handleCategoryClick = async (budget, category) => {
    try {
      if (!budget && activeBudget) {
        // If no budget provided but we have an active budget, use that
        setSelectedCategoryForDetails({
          ...category,
          budgetId: activeBudget._id
        });
        // Fetch fresh insights for the selected month
        const insightsResponse = await budgetService.getBudgetInsights(activeBudget._id, { month: selectedMonth });
        setInsights({ budgetInsights: { [activeBudget._id]: insightsResponse.data } });
      } else if (budget) {
        // If budget is provided, use it
        setSelectedCategoryForDetails({
          ...category,
          budgetId: budget._id
        });
        // Fetch fresh insights for the selected month
        const insightsResponse = await budgetService.getBudgetInsights(budget._id, { month: selectedMonth });
        setInsights({ budgetInsights: { [budget._id]: insightsResponse.data } });
      }
    } catch (err) {
      console.error('Error fetching category insights:', err);
      setError(err.message);
    }
  };

  const renderCategoryCard = (budget, category) => {
    const filteredInsights = getFilteredInsights();
    const categorySpent = filteredInsights?.budgetInsights?.[budget._id]?.categorySpent?.[category._id] || 0;
    const categoryAmount = category.isPercentage 
      ? ((Number(category.amount || 0) / 100) * Number(budget.totalBudget || 0))
      : Number(category.amount || 0);
    const spentPercentage = categoryAmount > 0 ? (categorySpent / categoryAmount) * 100 : 0;

    let progressColor = '#f5f5f5'; // even softer gray
    if (spentPercentage > 100) progressColor = colors.status.error;

    return (
      <Grid item xs={12} sm={6} md={3} lg={3} key={category._id}>
        <StyledCard 
          onClick={() => handleCategoryClick(budget, category)}
          sx={{ cursor: 'pointer' }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CategoryIcon>
                    <CategoryProgress
                      variant="determinate"
                      value={Math.min(spentPercentage, 100)}
                      size={72}
                      thickness={4}
                      sx={{
                        color: progressColor,
                        position: 'absolute'
                      }}
                    />
                    {getCategoryIcon(category.name)}
                  </CategoryIcon>
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {category.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.isPercentage 
                        ? `${Number(category.amount)}% ($${((Number(category.amount) / 100) * Number(budget?.totalBudget || 0)).toLocaleString()})`
                        : `$${Number(category.amount).toLocaleString()}`
                      }
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Spent: ${categorySpent.toLocaleString()}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: progressColor,
                      fontWeight: 500
                    }}
                  >
                    {spentPercentage.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryEditClick(budget, category);
                }}
                sx={{
                  color: colors.primary,
                  backgroundColor: `${colors.primary}10`,
                  '&:hover': {
                    backgroundColor: `${colors.primary}20`
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>
    );
  };

  const getFilteredInsights = () => {
    // Since insights are now fetched with the month parameter,
    // we can return them directly as they're already filtered
    return insights;
  };

  const getAllCategories = () => {
    if (!budgets || !Array.isArray(budgets)) {
      return [];
    }
    
    const allCategories = [];
    const seenNames = new Set();
    const filteredInsights = getFilteredInsights();
    
    budgets.forEach(budget => {
      if (!budget.categories) return;
      budget.categories.forEach(category => {
        if (!seenNames.has(category.name)) {
          seenNames.add(category.name);
          
          const categoryData = budgets.reduce((acc, b) => {
            const matchingCategory = b.categories?.find(c => c.name === category.name);
            if (matchingCategory) {
              const amount = matchingCategory.isPercentage 
                ? ((Number(matchingCategory.amount) / 100) * Number(b.totalBudget))
                : Number(matchingCategory.amount);
              acc.totalAmount += amount;
              
              const budgetInsights = filteredInsights?.budgetInsights?.[b._id];
              const spent = budgetInsights?.categorySpent?.[matchingCategory._id] || 0;
              acc.totalSpent += spent;
            }
            return acc;
          }, { totalAmount: 0, totalSpent: 0 });

          allCategories.push({
            ...category,
            totalAmount: categoryData.totalAmount,
            totalSpent: categoryData.totalSpent,
            budgetCount: budgets.filter(b => 
              b.categories?.some(c => c.name === category.name)
            ).length
          });
        }
      });
    });

    return allCategories.sort((a, b) => b.totalAmount - a.totalAmount);
  };

  const getTotalStats = () => {
    if (!budgets || !selectedMonth) return {
      totalBudget: 0,
      totalAllocated: 0,
      remaining: 0,
      percentageUsed: 0,
      avgTransaction: 0,
      transactionCount: 0
    };

    const totalBudget = budgets.reduce((sum, budget) => sum + Number(budget.totalBudget || 0), 0);
    const totalAllocated = budgets.reduce((sum, budget) => {
      return sum + (budget.categories || []).reduce((catSum, cat) => {
        const amount = cat.isPercentage
          ? (Number(cat.amount || 0) / 100) * Number(budget.totalBudget || 0)
          : Number(cat.amount || 0);
        return catSum + amount;
      }, 0);
    }, 0);

    let totalSpent = 0;
    let allTransactions = 0;

    const filteredInsights = getFilteredInsights();
    
    if (activeBudget && filteredInsights?.budgetInsights?.[activeBudget._id]) {
      // Single budget insights
      const budgetInsight = filteredInsights.budgetInsights[activeBudget._id];
      totalSpent = Object.values(budgetInsight.categorySpent || {}).reduce((sum, spent) => sum + spent, 0);
      allTransactions = budgetInsight.totalTransactions || 0;
    } else if (filteredInsights?.budgetInsights) {
      // All budgets insights
      Object.values(filteredInsights.budgetInsights).forEach(budgetInsight => {
        totalSpent += Object.values(budgetInsight.categorySpent || {}).reduce((sum, spent) => sum + spent, 0);
        allTransactions += budgetInsight.totalTransactions || 0;
      });
    }

    const remaining = totalAllocated - totalSpent;
    const avgTransaction = allTransactions > 0 ? totalSpent / allTransactions : 0;

    return {
      totalBudget,
      totalAllocated,
      remaining,
      percentageUsed: totalAllocated > 0 ? (totalSpent / totalAllocated) * 100 : 0,
      avgTransaction,
      transactionCount: allTransactions
    };
  };

  const handleBudgetClick = async (budget) => {
    try {
      if (activeBudget?._id === budget._id) {
        // If clicking the same budget, deselect it and show all budgets' insights
        setActiveBudget(null);
        const allInsights = { budgetInsights: {} };
        await Promise.all(budgets.map(async (b) => {
          const insightsResponse = await budgetService.getBudgetInsights(b._id, { month: selectedMonth });
          allInsights.budgetInsights[b._id] = insightsResponse.data;
        }));
        setInsights(allInsights);
      } else {
        // If selecting a different budget, update active budget and get its insights
        setActiveBudget(budget);
        const insightsResponse = await budgetService.getBudgetInsights(budget._id, { month: selectedMonth });
        setInsights({ budgetInsights: { [budget._id]: insightsResponse.data } });
      }
    } catch (err) {
      console.error('Error updating budget insights:', err);
      setError(err.message);
    }
  };

  const getTotalSpentForRange = (transactions, startDate, endDate) => {
    if (!transactions || !Array.isArray(transactions)) return 0;
    
    const filteredTransactions = transactions.filter(t => {
      const txDate = new Date(t.date);
      return txDate >= startDate && txDate <= endDate && t.transactionType === 'expense';
    });
    
    return filteredTransactions.reduce((total, t) => total + t.amount, 0);
  };

  const getTotalSpentToday = (transactions) => {
    if (!transactions || !Array.isArray(transactions)) return 0;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    return getTotalSpentForRange(transactions, today, tomorrow);
  };

  const getTotalSpentThisWeek = (transactions) => {
    if (!transactions || !Array.isArray(transactions)) return 0;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
    
    return getTotalSpentForRange(transactions, startOfWeek, endOfWeek);
  };

  const getTotalSpentThisMonth = (transactions, month = null) => {
    if (!transactions || !Array.isArray(transactions)) return 0;
    
    const now = new Date();
    let firstDayOfMonth, lastDayOfMonth;
    
    if (month) {
      const [year, monthNum] = month.split('-').map(Number);
      firstDayOfMonth = new Date(year, monthNum - 1, 1);
      lastDayOfMonth = new Date(year, monthNum, 0); // Last day of the month
    } else {
      firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    
    return getTotalSpentForRange(transactions, firstDayOfMonth, lastDayOfMonth);
  };

  const getBudgetRemaining = (budget) => {
    if (!budget) return 0;
    const allocated = (budget.categories || []).reduce((sum, cat) => {
      const amount = cat.isPercentage
        ? (Number(cat.amount || 0) / 100) * Number(budget.totalBudget || 0)
        : Number(cat.amount || 0);
      return sum + amount;
    }, 0);
    return Number(budget.totalBudget || 0) - allocated;
  };

  const getBudgetRemainingColor = (budget) => {
    const remaining = getBudgetRemaining(budget);
    if (remaining > 0) return colors.status.warning;
    if (remaining < 0) return colors.status.error;
    return colors.status.ready;
  };

  const getBudgetRemainingMessage = (budget) => {
    const remaining = getBudgetRemaining(budget);
    const totalBudget = Number(budget.totalBudget || 0);
    const allocatedPercentage = ((totalBudget - remaining) / totalBudget) * 100;
    
    if (remaining > 0) {
      return {
        message: 'Available',
        subMessage: `$${remaining.toFixed(0)}`,
        tooltip: `${allocatedPercentage.toFixed(1)}% allocated, $${remaining.toFixed(2)} still available`
      };
    }
    if (remaining < 0) {
      return {
        message: 'Over',
        subMessage: `$${Math.abs(remaining).toFixed(0)}`,
        tooltip: `${allocatedPercentage.toFixed(1)}% allocated, $${Math.abs(remaining).toFixed(2)} over budget`
      };
    }
    return {
      message: 'Balanced',
      subMessage: '100%',
      tooltip: 'All funds have been allocated'
    };
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ p: { xs: 3, sm: 6, md: 8 } }}>
        {/* Month Selector */}
        <Box sx={{ mb: 4 }}>
          <MonthSelector
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            availableMonths={availableMonths}
          />
        </Box>

        {/* Spending Overview */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <StatCard>
                <StatIcon>
                  <CalendarTodayIcon />
                </StatIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.875rem',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Spent Today
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.primary,
                      letterSpacing: '-0.5px',
                      mb: 0.5
                    }}
                  >
                    $<CountUp
                      end={getTotalSpentToday(transactions)}
                      decimals={2}
                      duration={1}
                      separator=","
                      preserveValue={true}
                    />
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard>
                <StatIcon>
                  <DateRangeIcon />
                </StatIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.875rem',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Spent This Week
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.primary,
                      letterSpacing: '-0.5px',
                      mb: 0.5
                    }}
                  >
                    $<CountUp
                      end={getTotalSpentThisWeek(transactions)}
                      decimals={2}
                      duration={1}
                      separator=","
                      preserveValue={true}
                    />
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard>
                <StatIcon>
                  <TrendingUpIcon />
                </StatIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.875rem',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Spent This Month
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.primary,
                      letterSpacing: '-0.5px',
                      mb: 0.5
                    }}
                  >
                    $<CountUp
                      end={getTotalSpentThisMonth(transactions, selectedMonth)}
                      decimals={2}
                      duration={1}
                      separator=","
                      preserveValue={true}
                    />
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ py: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 5 }}>
            <Grid item xs={12} md={4}>
              <StatCard>
                <StatIcon>
                  <AccountBalanceIcon />
                </StatIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.875rem',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Total Budget
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.primary,
                      letterSpacing: '-0.5px',
                      mb: 0.5
                    }}
                  >
                    $<CountUp
                      end={getTotalStats().totalBudget}
                      decimals={2}
                      duration={1}
                      separator=","
                      preserveValue={true}
                    />
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard>
                <StatIcon>
                  <TrendingUpIcon />
                </StatIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.875rem',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Total Spent
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.primary,
                      letterSpacing: '-0.5px',
                      mb: 0.5
                    }}
                  >
                    $<CountUp
                      end={getTotalStats().totalAllocated - getTotalStats().remaining}
                      decimals={2}
                      duration={1}
                      separator=","
                      preserveValue={true}
                    />
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard>
                <StatIcon>
                  <SavingsIcon />
                </StatIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.875rem',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Remaining
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.primary,
                      letterSpacing: '-0.5px',
                      mb: 0.5
                    }}
                  >
                    $<CountUp
                      end={getTotalStats().remaining}
                      decimals={2}
                      duration={1}
                      separator=","
                      preserveValue={true}
                    />
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
          </Grid>

          {/* Budget Selection */}
          {Array.isArray(budgets) && budgets.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Your Budgets
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowBudgetForm(true)}
                  sx={{
                    backgroundColor: colors.primary,
                    '&:hover': {
                      backgroundColor: colors.primaryDark
                    }
                  }}
                >
                  Create Budget
                </Button>
              </Box>
              <Grid container spacing={2}>
                {budgets.map((budget) => (
                  <Grid item xs={12} sm={6} md={4} key={budget._id}>
                    <BudgetCard 
                      active={activeBudget?._id === budget._id}
                      onClick={() => handleBudgetClick(budget)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {budget.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {budget.period.charAt(0).toUpperCase() + budget.period.slice(1)} Budget
                          </Typography>
                          <Typography variant="h6" sx={{ mt: 1, color: colors.primary }}>
                            ${Number(budget.totalBudget).toLocaleString()}
                          </Typography>
                        </Box>
                        {getBudgetRemaining(budget) !== 0 && (
                          <Tooltip 
                            title={getBudgetRemainingMessage(budget).tooltip}
                            arrow
                            placement="left"
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              alignItems: 'center',
                              backgroundColor: `${getBudgetRemainingColor(budget)}10`,
                              borderRadius: '12px',
                              padding: '16px',
                              minWidth: '120px'
                            }}>
                              <CircularProgressWrapper sx={{ marginRight: 0, marginBottom: 1 }}>
                                <CircularProgress
                                  variant="determinate"
                                  value={((Number(budget.totalBudget) - getBudgetRemaining(budget)) / Number(budget.totalBudget)) * 100}
                                  size={80}
                                  thickness={4}
                                  sx={{
                                    color: getBudgetRemainingColor(budget),
                                    '& .MuiCircularProgress-circle': {
                                      strokeLinecap: 'round'
                                    }
                                  }}
                                />
                                <CircularProgressLabel>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      color: getBudgetRemainingColor(budget), 
                                      fontWeight: 600,
                                      fontSize: '1.1rem'
                                    }}
                                  >
                                    {((Number(budget.totalBudget) - getBudgetRemaining(budget)) / Number(budget.totalBudget) * 100).toFixed(0)}%
                                  </Typography>
                                </CircularProgressLabel>
                              </CircularProgressWrapper>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: getBudgetRemainingColor(budget),
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mb: 0.5
                                  }}
                                >
                                  {getBudgetRemainingMessage(budget).message}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: getBudgetRemainingColor(budget),
                                    opacity: 0.8,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {getBudgetRemainingMessage(budget).subMessage}
                                </Typography>
                              </Box>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </BudgetCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Active Budget View or All Categories */}
          {activeBudget ? (
            <>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                Budget Categories
              </Typography>
              <Grid container spacing={3}>
                {(activeBudget.categories || []).map((category) => {
                  return renderCategoryCard(activeBudget, category);
                })}
              </Grid>
            </>
          ) : Array.isArray(budgets) && budgets.length > 0 ? (
            <>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                All Categories
              </Typography>
              <Grid container spacing={4}>
                {getAllCategories().map((category) => {
                  const spent = category.totalSpent;
                  const budgeted = category.totalAmount;
                  const available = Math.max(budgeted - spent, 0);
                  const spentPercentage = budgeted > 0 ? (spent / budgeted) * 100 : 0;
                  const isOver = spentPercentage > 100;
                  const progressColor = isOver ? colors.status.error : colors.status.success;
                  return (
                    <Grid item xs={12} sm={6} md={3} lg={3} key={category._id}>
                      <StyledCard
                        sx={{
                          width: 220,
                          height: 280,
                          minWidth: 220,
                          minHeight: 280,
                          maxWidth: 220,
                          maxHeight: 280,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          py: 2,
                          px: 1,
                          mx: 'auto',
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center', mb: 0.5 }}>
                          {category.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                          Budget: ${budgeted.toLocaleString()}
                        </Typography>
                        <CardContent sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <CategoryCardCircle>
                              {/* Gray background ring always visible */}
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={200}
                                thickness={3}
                                sx={{
                                  color: '#f5f5f5',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  zIndex: 1,
                                  '& .MuiCircularProgress-circle': {
                                    strokeLinecap: 'round',
                                  },
                                }}
                              />
                              {/* Colored progress on top */}
                              <CircularProgress
                                variant="determinate"
                                value={Math.min(spentPercentage, 100)}
                                size={200}
                                thickness={3}
                                sx={{
                                  color: progressColor,
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  zIndex: 2,
                                  '& .MuiCircularProgress-circle': {
                                    strokeLinecap: 'round',
                                  },
                                }}
                              />
                              <CategoryCardInner>
                                <CategoryCardIcon>
                                  {ICONS[category.icon] || ICONS.other}
                                </CategoryCardIcon>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.status.success, mt: 1, mb: 0 }}>
                                  ${available.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                  to spend
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.status.error, fontWeight: 500, mb: 0 }}>
                                  ${spent.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  spent
                                </Typography>
                              </CategoryCardInner>
                            </CategoryCardCircle>
                          </Box>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              backgroundColor: `${colors.primary}05`,
              borderRadius: '20px'
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                No budgets yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Create your first budget to start tracking your expenses
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingBudget(null);
                  setShowBudgetForm(true);
                }}
              >
                Create Budget
              </Button>
            </Box>
          )}
        </Box>
      </Container>

      <BudgetForm
        open={showBudgetForm || !!editingBudget}
        onClose={() => {
          setShowBudgetForm(false);
          setEditingBudget(null);
        }}
        onSubmit={editingBudget ? handleUpdateBudget : handleCreateBudget}
        onDelete={handleDeleteBudget}
        initialData={editingBudget}
      />

      <BudgetSelectorModal
        open={showBudgetSelector}
        onClose={() => {
          setShowBudgetSelector(false);
          setSelectedCategoryForBudgetChoice(null);
        }}
        budgets={budgets}
        category={selectedCategoryForBudgetChoice}
        onBudgetSelect={handleBudgetSelect}
      />

      {selectedCategory && (
        <BudgetCategoryEditModal
          open={isCategoryEditModalOpen}
          onClose={() => setIsCategoryEditModalOpen(false)}
          category={selectedCategory}
          onSave={handleCategoryUpdate}
        />
      )}

      {selectedCategoryForDetails && (
        <BudgetCategoryDetailsModal
          open={!!selectedCategoryForDetails}
          onClose={() => setSelectedCategoryForDetails(null)}
          category={selectedCategoryForDetails}
          budget={activeBudget}
          insights={insights}
          selectedMonth={selectedMonth}
          budgets={budgets}
        />
      )}
    </Layout>
  );
};

export default Budgets;
